import { FC, useState, useEffect } from 'react'
import styled from 'styled-components'
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro'

import { ONBOARDING_CARD } from './copyConstants'
import { Card, Text, Icon } from '../../components/BaseComponents'
import { Colors } from '../../styles/theme'
import { Image } from 'semantic-ui-react'
import { getCurrentUser } from '../../selectors/user.selectors'
import { useReselector } from '../../utils/sharedHooks'

const StepContainer = styled.div`
  display: flex;
  gap: 12px;
  border-radius: 8px;
  align-items: flex-start;
`

const StepNumberCircle = styled.div`
  display: flex;
  align-items: center;
  font-size: 12px;
  justify-content: center;
  width: 24px;
  height: 24px;
  min-width: 24px;
  background-color: ${Colors.moss};
  color: ${Colors.black};
  border-radius: 50%;
`

const StepContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
`

const DismissButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  justify-content: center;
  position: absolute;
  top: 16px;
  right: 16px;
  color: ${Colors.darkGray};
`

const steps = [
  {
    number: 1,
    title: 'Mark personal transactions',
    instructions: (
      <>
        <li>For each personal transaction, go to the Type column.</li>
        <li>
          Choose <strong>Personal</strong> from the drop-down menu. This
          excludes the transaction from your business books.{'  '}
          <a
            href="https://support.joinheard.com/hc/en-us/articles/23407015266967-Using-a-dedicated-business-bank-account-for-your-practice#risks_of_commingled_account"
            target="_blank"
            rel="noopener noreferrer"
          >
            Why is this important?
          </a>
        </li>
      </>
    ),
  },
  {
    number: 2,
    title: 'Provide context on unclear transactions',
    instructions: (
      <>
        <li>
          Click on the <strong>Needs Review</strong> tab to see all unclear
          transactions.
        </li>
        <li>
          Click the <strong>•••</strong> menu and add a note explaining what the
          transaction was used for.
        </li>
        <li>A bookkeeper will categorize the transaction accordingly.</li>
      </>
    ),
  },
]
const TransactionsOnboardingCard: FC = () => {
  const [isVisible, setIsVisible] = useState(false)
  const currentUser = useReselector(getCurrentUser)

  const userSpecificKey = currentUser?.id
    ? `${ONBOARDING_CARD.LOCAL_STORAGE_KEY}-user-${currentUser.id}`
    : ONBOARDING_CARD.LOCAL_STORAGE_KEY

  useEffect(() => {
    const isDismissed = localStorage.getItem(userSpecificKey)

    if (!isDismissed) {
      setIsVisible(true)
    }
  }, [userSpecificKey])

  const handleDismiss = () => {
    localStorage.setItem(userSpecificKey, 'true')
    setIsVisible(false)
  }

  if (!isVisible) {
    return null
  }

  return (
    <Card
      type="section"
      backgroundColor="natural"
      style={{
        borderColor: Colors.blush,
        display: 'flex',
        gap: '32px',
        flexDirection: 'row',
      }}
    >
      <Card.Content
        style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}
      >
        <DismissButton onClick={handleDismiss}>
          <Icon icon={regular('close')} size="xl" />
        </DismissButton>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Text as="eyebrow" color="darkGray">
            {ONBOARDING_CARD.SECTION_LABEL}
          </Text>
          <Text as="h2" style={{ textTransform: 'none' }}>
            {ONBOARDING_CARD.TITLE}
          </Text>
        </div>

        <Text as="bodyMd">{ONBOARDING_CARD.BODY}</Text>

        {steps.map((step, index) => (
          <StepContainer key={step.title}>
            <StepNumberCircle>{index + 1}</StepNumberCircle>
            <StepContent>
              <Text as="h3">{step.title}</Text>
              <Text as="bodySm" style={{ color: Colors.charcoal }}>
                <ol style={{ paddingInlineStart: '1.5em' }}>
                  {step.instructions}
                </ol>
              </Text>
            </StepContent>
          </StepContainer>
        ))}
      </Card.Content>

      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          borderRadius: '16px',
          alignSelf: 'flex-end',
        }}
      >
        <Image
          src="https://heard-images.s3.amazonaws.com/assets/transaction-type-dropdown.png"
          alt=""
          style={{
            objectFit: 'contain',
            borderRadius: '16px',
          }}
        />
      </div>
    </Card>
  )
}

export default TransactionsOnboardingCard
